"use client";
import { AnimatePresence, motion } from "framer-motion";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";
import { useMotionPreference } from "../../hooks/useMotionPreference";
import { OptimizedGlass } from "../../primitives";
import { useA11yId } from "../../utils/a11y";
import { useGlassSound } from "../../utils/soundDesign";
import {
  ContrastGuard,
  TextWithContrast,
} from "@/components/accessibility/ContrastGuard";

export interface GoldenRatioItem {
  id: string;
  content: React.ReactNode;
  priority?: number;
  category?: string;
  aspectRatio?: number;
  minSize?: { width: number; height: number };
  maxSize?: { width: number; height: number };
  metadata?: Record<string, unknown>;
}

export interface GoldenRatioSection {
  width: number;
  height: number;
  x: number;
  y: number;
  item?: GoldenRatioItem;
  level: number;
  ratio: number;
}

export interface GlassGoldenRatioGridProps {
  items: GoldenRatioItem[];
  containerWidth?: number;
  containerHeight?: number;
  goldenRatio?: number;
  subdivisionLevels?: number;
  spacing?: number;
  animateLayout?: boolean;
  showGrid?: boolean;
  showRatioLines?: boolean;
  showEmptyLabels?: boolean;
  responsive?: boolean;
  onItemClick?: (item: GoldenRatioItem) => void;
  onItemHover?: (item: GoldenRatioItem | null) => void;
  onLayoutChange?: (sections: GoldenRatioSection[]) => void;
  glassConfig?: {
    blur?: number;
    opacity?: number;
    saturation?: number;
    brightness?: number;
    contrast?: number;
  };
  soundEnabled?: boolean;
  compact?: boolean;
  contained?: boolean;
  maxHeight?: number | string;
  className?: string;
  style?: React.CSSProperties;
}

export const GlassGoldenRatioGrid = forwardRef<
  HTMLDivElement,
  GlassGoldenRatioGridProps
>(
  (
    {
      // ContrastGuard layout text coverage is tracked in the manual accessibility QA report.

      items = [],
      containerWidth = 800,
      containerHeight = 600,
      goldenRatio = 1.618,
      subdivisionLevels = 4,
      spacing = 8,
      animateLayout = true,
      showGrid = false,
      showRatioLines = true,
      showEmptyLabels = false,
      responsive = true,
      onItemClick,
      onItemHover,
      onLayoutChange,
      glassConfig = {},
      soundEnabled = true,
      compact = false,
      contained = false,
      maxHeight,
      className = "",
      style = {},
      ...props
    },
    ref
  ) => {
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);
    const [selectedItem, setSelectedItem] = useState<string | null>(null);
    const [dimensions, setDimensions] = useState({
      width: containerWidth,
      height: containerHeight,
    });
    const containerRef = useRef<HTMLDivElement>(null);

    const { prefersReducedMotion } = useMotionPreference();
    const gridId = useA11yId();
    const { play } = useGlassSound();
    const effectiveWidth = compact ? 360 : containerWidth;
    const effectiveHeight = compact ? 220 : containerHeight;
    const effectiveLevels = compact
      ? Math.min(subdivisionLevels, 3)
      : subdivisionLevels;
    const effectiveShowGrid = compact ? false : showGrid;
    const effectiveShowRatioLines = compact ? false : showRatioLines;
    const boundedHeight =
      maxHeight ?? (compact || contained ? effectiveHeight : undefined);

    // Responsive dimension handling
    useEffect(() => {
      if (!responsive || !containerRef.current) return;

      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          const { width, height } = entry.contentRect;
          setDimensions({ width, height });
        }
      });

      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    }, [responsive]);

    // Generate golden ratio subdivisions
    const generateGoldenSections = useCallback((): GoldenRatioSection[] => {
      const sections: GoldenRatioSection[] = [];
      const queue: Array<{
        x: number;
        y: number;
        width: number;
        height: number;
        level: number;
      }> = [
        {
          x: 0,
          y: 0,
          width: compact ? effectiveWidth : dimensions.width,
          height: compact ? effectiveHeight : dimensions.height,
          level: 0,
        },
      ];

      while (
        queue.length > 0 &&
        sections.length < Math.pow(2, effectiveLevels)
      ) {
        const current = queue.shift()!;

        if (current.level >= effectiveLevels) {
          sections.push({
            ...current,
            ratio: current.width / current.height,
          });
          continue;
        }

        // Apply golden ratio subdivision
        const isHorizontalSplit = current.width > current.height;

        if (isHorizontalSplit) {
          // Split horizontally using golden ratio
          const majorWidth = current.width / goldenRatio;
          const minorWidth = current.width - majorWidth;

          queue.push({
            x: current.x,
            y: current.y,
            width: majorWidth,
            height: current.height,
            level: current.level + 1,
          });

          queue.push({
            x: current.x + majorWidth,
            y: current.y,
            width: minorWidth,
            height: current.height,
            level: current.level + 1,
          });
        } else {
          // Split vertically using golden ratio
          const majorHeight = current.height / goldenRatio;
          const minorHeight = current.height - majorHeight;

          queue.push({
            x: current.x,
            y: current.y,
            width: current.width,
            height: majorHeight,
            level: current.level + 1,
          });

          queue.push({
            x: current.x,
            y: current.y + majorHeight,
            width: current.width,
            height: minorHeight,
            level: current.level + 1,
          });
        }
      }

      return sections;
    }, [
      compact,
      dimensions,
      effectiveHeight,
      effectiveLevels,
      effectiveWidth,
      goldenRatio,
    ]);

    // Assign items to sections based on priority and size requirements
    const assignItemsToSections = useCallback(
      (sections: GoldenRatioSection[]): GoldenRatioSection[] => {
        const sortedItems = [...items].sort(
          (a, b) => (b.priority || 0) - (a.priority || 0)
        );
        const sortedSections = [...sections].sort(
          (a, b) => b.width * b.height - a.width * a.height
        );

        const assignedSections = sortedSections.map((section, index) => {
          const item = sortedItems[index];
          return {
            ...section,
            item: item || undefined,
          };
        });

        return assignedSections;
      },
      [items]
    );

    const sections = useMemo(() => {
      const goldenSections = generateGoldenSections();
      const assignedSections = assignItemsToSections(goldenSections);

      // Notify about layout changes
      onLayoutChange?.(assignedSections);

      return assignedSections;
    }, [generateGoldenSections, assignItemsToSections, onLayoutChange]);

    // Generate spiral golden ratio curve points
    const generateGoldenSpiral = useCallback((): Array<{
      x: number;
      y: number;
    }> => {
      const points: Array<{ x: number; y: number }> = [];
      const centerX = dimensions.width / 2;
      const centerY = dimensions.height / 2;
      const maxRadius = Math.min(dimensions.width, dimensions.height) / 3;

      for (let i = 0; i < 200; i++) {
        const angle = i * 0.2;
        const radius =
          (maxRadius / 20) * Math.pow(goldenRatio, angle / (Math.PI * 2));

        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);

        if (
          x >= 0 &&
          x <= dimensions.width &&
          y >= 0 &&
          y <= dimensions.height
        ) {
          points.push({ x, y });
        }
      }

      return points;
    }, [dimensions, goldenRatio]);

    const spiralPoints = useMemo(
      () => generateGoldenSpiral(),
      [generateGoldenSpiral]
    );

    const handleItemClick = useCallback(
      (item: GoldenRatioItem) => {
        setSelectedItem(item.id);
        onItemClick?.(item);

        if (soundEnabled) {
          play("click");
        }
      },
      [onItemClick, soundEnabled, play]
    );

    const handleItemHover = useCallback(
      (item: GoldenRatioItem | null) => {
        setHoveredItem(item?.id || null);
        onItemHover?.(item);

        if (soundEnabled && item) {
          play("hover");
        }
      },
      [onItemHover, soundEnabled, play]
    );

    const getItemVariants = () => ({
      hidden: {
        scale: 0,
        opacity: 0,
      },
      visible: (delay: number) => ({
        scale: 1,
        opacity: 1,
        transition: {
          type: "spring",
          tension: 300,
          friction: 25,
          delay: prefersReducedMotion ? 0 : delay * 0.05,
        },
      }),
      hover: {
        scale: 1.02,
        transition: {
          type: "spring",
          tension: 400,
          friction: 20,
        },
      },
      selected: {
        scale: 1.05,
        transition: {
          type: "spring",
          tension: 400,
          friction: 20,
        },
      },
    });

    return (
      <OptimizedGlass
        ref={ref}
        className={`glass-golden-ratio-grid relative overflow-auto ${className}`}
        style={{
          width: responsive ? "100%" : effectiveWidth,
          minWidth: responsive || compact ? undefined : effectiveWidth,
          height: compact
            ? effectiveHeight
            : responsive
              ? "100%"
              : effectiveHeight,
          minHeight: compact ? effectiveHeight : effectiveHeight,
          maxHeight:
            typeof boundedHeight === "number"
              ? `${boundedHeight}px`
              : boundedHeight,
          overflowX: "auto",
          overflowY: "auto",
          ...style,
        }}
        glassConfig={{
          blur: 12,
          opacity: 0.95,
          saturation: 1.05,
          brightness: 1.02,
          ...glassConfig,
        }}
        role="grid"
        aria-label="Golden ratio layout grid"
        id={gridId}
        {...props}
      >
        <div
          ref={containerRef}
          className="glass-absolute glass-inset-0"
          style={{
            width: compact ? effectiveWidth : dimensions.width,
            height: compact ? effectiveHeight : dimensions.height,
          }}
        >
          {/* Golden spiral overlay */}
          {effectiveShowRatioLines && (
            <svg
              className="glass-absolute glass-inset-0 glass-pointer-events-none"
              width={dimensions.width}
              height={dimensions.height}
            >
              <path
                d={`M ${spiralPoints.map((p: any) => `${p.x},${p.y}`).join(" L ")}`}
                stroke="rgba(255, 215, 0, 0.3)"
                strokeWidth="2"
                fill="none"
                strokeDasharray="5,5"
              />
            </svg>
          )}

          {/* Grid lines */}
          {effectiveShowGrid && (
            <svg
              className="glass-absolute glass-inset-0 glass-pointer-events-none"
              width={dimensions.width}
              height={dimensions.height}
            >
              {sections.map((section, index) => (
                <rect
                  key={`grid-${index}`}
                  x={section.x}
                  y={section.y}
                  width={section.width}
                  height={section.height}
                  stroke="rgba(var(--glass-color-white) / var(--glass-opacity-20))"
                  strokeWidth="1"
                  fill="none"
                  strokeDasharray="3,3"
                />
              ))}
            </svg>
          )}

          {/* Grid sections with items */}
          <AnimatePresence>
            {sections.map((section, index) => {
              const hasItem = !!section.item;
              const isHovered = hasItem && hoveredItem === section.item!.id;
              const isSelected = hasItem && selectedItem === section.item!.id;

              return (
                <motion.div
                  key={`section-${index}`}
                  className={`
                    absolute overflow-visible
                    ${hasItem ? "cursor-pointer" : "cursor-default"}
                  `}
                  style={{
                    left: section.x + spacing / 2,
                    top: section.y + spacing / 2,
                    width: section.width - spacing,
                    height: section.height - spacing,
                    boxSizing: "border-box",
                  }}
                  custom={index}
                  variants={getItemVariants()}
                  initial="hidden"
                  animate={
                    isSelected ? "selected" : isHovered ? "hover" : "visible"
                  }
                  exit="hidden"
                  onMouseEnter={() => hasItem && handleItemHover(section.item!)}
                  onMouseLeave={() => handleItemHover(null)}
                  onClick={() => hasItem && handleItemClick(section.item!)}
                  role="row"
                >
                  {hasItem ? (
                    <div
                      className={`
                                          w-full h-full box-border glass-surface rounded-lg border border-white/20
                                          glass-backdrop-blur-md transition-all duration-200 p-3
                                          flex min-w-0 min-h-0 items-center justify-center text-center
                                          ${
                                            isHovered || isSelected
                                              ? "bg-white/20 border-white/40"
                                              : "bg-white/10 border-white/20"
                                          }
                                        `}
                      style={{
                        width: "100%",
                        height: "100%",
                        boxSizing: "border-box",
                        overflow: "hidden",
                      }}
                      role="gridcell"
                    >
                      {section.item!.content}
                    </div>
                  ) : (
                    <div
                      className="glass-w-full glass-h-full glass-border glass-border-dashed glass-border-white/10 glass-radius-lg glass-flex glass-items-center glass-justify-center glass-text-primary-glass-opacity-30"
                      style={{
                        width: "100%",
                        height: "100%",
                        boxSizing: "border-box",
                        overflow: "hidden",
                      }}
                      role="gridcell"
                    >
                      {showEmptyLabels && (
                        <div className="glass-text-xs">Empty</div>
                      )}
                    </div>
                  )}
                  {/* Section info overlay */}
                  {(effectiveShowGrid || (isHovered && !compact)) && (
                    <div
                      className="glass-absolute glass-top-1 glass-left-1 glass-surface-dark/50 glass-text-primary glass-text-xs glass-px-1 glass-py-0.5 glass-radius glass-backdrop-blur-sm glass-contrast-guard"
                      data-glass-overlay="true"
                    >
                      {section.ratio.toFixed(2)}
                    </div>
                  )}

                  {/* Golden ratio indicator */}
                  {Math.abs(section.ratio - goldenRatio) < 0.1 && (
                    <div
                      className="glass-absolute glass-top-1 glass-right-1 glass-w-2 glass-h-2 glass-surface-yellow glass-radius-full glass-opacity-70"
                      data-glass-overlay="true"
                    />
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Statistics panel */}
        {!compact && (
          <div className="glass-absolute glass-bottom-4 glass-left-4 glass-flex glass-flex-col glass-gap-1 glass-text-xs glass-text-primary-opacity-70">
            <div className="glass-surface-dark/20 glass-px-2 glass-py-1 glass-radius glass-backdrop-blur-sm glass-contrast-guard">
              Sections: {sections.length}
            </div>
            <div className="glass-surface-dark/20 glass-px-2 glass-py-1 glass-radius glass-backdrop-blur-sm glass-contrast-guard">
              Items: {items.length}
            </div>
            <div className="glass-surface-dark/20 glass-px-2 glass-py-1 glass-radius glass-backdrop-blur-sm glass-contrast-guard">
              Golden Ratio: {goldenRatio}
            </div>
            <div className="glass-surface-dark/20 glass-px-2 glass-py-1 glass-radius glass-backdrop-blur-sm glass-contrast-guard">
              Levels: {effectiveLevels}
            </div>
          </div>
        )}

        {/* Legend */}
        {!compact && (
          <div className="glass-absolute glass-top-4 glass-right-4 glass-flex glass-flex-col glass-gap-1 glass-text-xs glass-text-primary-opacity-70">
            <div className="glass-flex glass-items-center glass-gap-2 glass-surface-dark/20 glass-px-2 glass-py-1 glass-radius glass-backdrop-blur-sm glass-contrast-guard">
              <div className="glass-w-2 glass-h-2 glass-surface-yellow glass-radius-full" />
              Golden Ratio
            </div>
            {effectiveShowRatioLines && (
              <div className="glass-flex glass-items-center glass-gap-2 glass-surface-dark/20 glass-px-2 glass-py-1 glass-radius glass-backdrop-blur-sm glass-contrast-guard">
                <div
                  className="glass-w-4 glass-h-0-5 glass-surface-yellow glass-opacity-50"
                  style={{
                    background:
                      "repeating-linear-gradient(to right, var(--glass-color-warning-light) 0, var(--glass-color-warning-light) 3px, transparent 3px, transparent 6px)",
                  }}
                />
                Spiral
              </div>
            )}
          </div>
        )}
      </OptimizedGlass>
    );
  }
);

GlassGoldenRatioGrid.displayName = "GlassGoldenRatioGrid";
